import { cloneDeep } from "lodash";
import * as React from "react";
import { useDispatch } from "react-redux";
import { uid } from "uid";

import { TextInput } from "../../../components/Forms/Base/Fields";
import StandardModal from "../../../components/Modals/StandardModal";
import {
  setActiveFormInstance,
  updateActiveFormInstance,
  useFormError,
  useFormInstanceField,
} from "../../../store/base";

import {
  constants,
  update as updateCompanyInvoicing,
} from "../../../store/invoicingCompany";
import { addToast, TOAST_TYPES } from "../../../store/toasts";
import Table from "../../Billecta/Table/BasicTable";
import DetailInfo from "../../Details/OverviewInfo/DetailInfo/DetailInfo";
import {
  OverviewSubtitle,
  OverviewTitle,
  OverviewTitleWithSubtitleWrapper,
  OverviewTitleWrapper,
} from "../../Details/OverviewInfo/styles";
import { PrimaryButton, TextButton } from "../../Forms/Base/Buttons";
import OverlaySpinner from "../../Loaders/OverlaySpinner";
import { InnerBox } from "../../sharedStyles";

export default function HandleForeignAccounts({ companyInvoiceConfig }) {
  const dispatch = useDispatch();

  const method = "PATCH";
  const storeName = constants.STORE_NAME;

  const [handleCostId, setHandleCostId] = React.useState(null);
  const [loadingPayments, setLoadingPayments] = React.useState(false);

  const formCosts = useFormInstanceField({
    storeName,
    fieldKey: "companyforeignaccount_set",
  });
  const costErrors = useFormError({
    storeName,
    fieldKey: "companyforeignaccount_set",
  });

  const tableColumns = React.useMemo(() => {
    return [
      {
        Header: "Bank",
        accessor: "bank_name",
        Cell: ({ value }) => <div>{value}</div>,
      },
      {
        Header: "IBAN",
        accessor: "iban",
        Cell: ({ value }) => <div>{value}</div>,
      },
      {
        Header: "BIC",
        accessor: "bic",
        Cell: ({ value }) => <div>{value}</div>,
      },
    ];
  }, [companyInvoiceConfig]);

  const onUpdate = () => {
    setEditActive(false);
    dispatch(
      addToast({
        type: TOAST_TYPES.INFO,
        title: "Raden uppdaterades",
      })
    );
    setHandleCostId(null);
  };

  const onRemove = (id) => {
    const costsCopy = cloneDeep(formCosts);

    const idx = costsCopy.findIndex((c) => (c.id || c._tempId) === id);

    costsCopy.splice(idx, 1);

    setHandleCostId(null);

    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          companyforeignaccount_set: costsCopy,
        },
      })
    );

    dispatch(
      addToast({
        type: TOAST_TYPES.INFO,
        title: "Raden togs bort",
      })
    );
  };

  const handleEditCost = (row) => {
    setHandleCostId(row.original.id || row.original._tempId);
  };

  const checkRowError = (row) => {
    const errorIndexes =
      costErrors
        ?.map((r, idx) => {
          if (Object.keys(r).length) {
            return idx;
          }
          return null;
        })
        ?.filter((e) => e != null) || [];
    return errorIndexes.includes(row.index);
  };

  const onUpdateSuccess = (_, returnedData) => {
    setLoadingPayments(false);

    dispatch(
      setActiveFormInstance({
        storeName,
        data: returnedData,
      })
    );
  };
  const onUpdateError = () => {
    setLoadingPayments(false);
  };

  const preProcess = (data) => {
    const dataClone = cloneDeep(data);

    if (!!dataClone.companyforeignaccount_set?.length) {
      dataClone.companyforeignaccount_set.forEach((c) => {
        c.config = { id: companyInvoiceConfig.id };
      });
    }

    return dataClone;
  };

  const updateCosts = () => {
    setLoadingPayments(true);
    dispatch(
      updateCompanyInvoicing({
        id: companyInvoiceConfig.id,
        successCallback: onUpdateSuccess,
        errorCallback: onUpdateError,
        preProcess: preProcess,
      })
    );
  };

  const canUpdate = checkCanUpdatePayments({
    formCosts,
    object: companyInvoiceConfig,
  });

  const handleAddCost = () => {
    const costsCopy = [...(formCosts || [])];
    const tempId = uid();

    costsCopy.push({ _tempId: tempId });
    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          companyforeignaccount_set: costsCopy,
        },
      })
    );

    setHandleCostId(tempId);
  };

  const currentCost = formCosts?.find(
    (c) => c.id === handleCostId || c._tempId === handleCostId
  );
  const currentCostIndex = formCosts?.findIndex(
    (c) => c.id === handleCostId || c._tempId === handleCostId
  );

  const [editActive, setEditActive] = React.useState(false);

  // Set new costs to edit
  React.useEffect(() => {
    if (handleCostId != null && currentCost) {
      setEditActive(true);
    }
  }, [handleCostId, currentCost]);

  const infoObj = {
    Detaljer: [
      {
        title: "Bank",
        value: currentCost?.bank_name,
      },
      {
        title: "IBAN",
        value: currentCost?.iban,
      },
      {
        title: "BIC",
        value: currentCost?.bic,
      },
    ],
  };

  return (
    <>
      <StandardModal
        isOpen={handleCostId !== null}
        closeFunction={
          editActive ? () => setEditActive(false) : () => setHandleCostId(null)
        }
        title={`${editActive ? "Redigera" : "Hantera"} konto`}
        withActionBar
        actionBarAcceptTitle={editActive ? "Klar" : "Redigera"}
        actionBarCancelTitle={editActive ? "Tillbaka" : "Stäng"}
        saveFunction={editActive ? onUpdate : () => setEditActive(true)}
      >
        {!editActive ? (
          <>
            <DetailInfo infoObj={infoObj} />

            <TextButton
              title="Radera rad"
              clicked={() => onRemove(handleCostId)}
              red
              iconType="close"
              iconPlacement="right"
              extraStyle={{ marginTop: 24 }}
            />
          </>
        ) : (
          <>
            <TextInput
              title="Bank"
              storeName={storeName}
              method={method}
              fieldKey={`companyforeignaccount_set[${currentCostIndex}].bank_name`}
              instructionsKey="companyforeignaccount_set.bank_name"
            />
            <TextInput
              extraStyles={{ minWidth: "150px", marginRight: "20px" }}
              title="BIC/Swift"
              description="BIC/Swift är det internationella ID:t för banken"
              {...{
                storeName,
                method,
                fieldKey: `companyforeignaccount_set[${currentCostIndex}].bic`,
                instructionsKey: "companyforeignaccount_set.bic",
              }}
            />

            <TextInput
              extraStyles={{ minWidth: "290px" }}
              title="IBAN"
              description="IBAN är det internationella formatet för bankkontot"
              {...{
                storeName,
                method,
                fieldKey: `companyforeignaccount_set[${currentCostIndex}].iban`,
                instructionsKey: "companyforeignaccount_set.iban",
              }}
            />
          </>
        )}
      </StandardModal>

      {loadingPayments && <OverlaySpinner />}
      <OverviewTitleWrapper>
        <OverviewTitleWithSubtitleWrapper>
          <OverviewTitle small>Utländska konton och P27</OverviewTitle>
          <OverviewSubtitle>
            Tryck på en rad för att redigera den. För att spara ändringar, tryck
            på "Spara uppdateringar" längst ner.
          </OverviewSubtitle>
        </OverviewTitleWithSubtitleWrapper>
        <PrimaryButton title="Lägg till rad" clicked={handleAddCost} />
      </OverviewTitleWrapper>

      {!!formCosts?.length ? (
        <>
          <Table
            onRowClicked={handleEditCost}
            columns={tableColumns}
            data={formCosts}
            checkRowError={checkRowError}
            hideSearch
          />
        </>
      ) : (
        <InnerBox
          style={{
            minHeight: 200,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Inga konton tillagda
        </InnerBox>
      )}
      {canUpdate && (
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}
        >
          <PrimaryButton
            pulse
            title="Spara uppdateringar"
            clicked={updateCosts}
          />
        </div>
      )}
    </>
  );
}

function checkCanUpdatePayments({ formCosts, object }) {
  const existingCosts = object?.companyforeignaccount_set;

  if (!existingCosts?.length && !formCosts?.length) return false;

  let hasChange = false;

  if (existingCosts?.length !== formCosts?.length) return true;
  if (!existingCosts?.length && !formCosts?.length) return false;

  existingCosts.forEach((cost) => {
    const match = formCosts.find((fCost) => {
      return fCost.id === cost.id;
    });

    if (!match) {
      hasChange = true;
      return;
    }

    if (
      match.bankgiro !== cost.bankgiro ||
      match.plusgiro !== cost.plusgiro ||
      match.bank_account !== cost.bank_account ||
      match.clearing_number !== cost.clearing_number ||
      match.iban !== cost.iban ||
      match.bic !== cost.bic ||
      match.primary !== cost.primary
    ) {
      hasChange = true;
    }
  });

  return hasChange;
}
